import '@/styles/globals.css'
import 'aos/dist/aos.css'
import '@/shared/utils/axios'
import '@/styles/Main.module.scss'
import '@mantine/core/styles.css'
import 'react-toastify/dist/ReactToastify.css'

import { MantineProvider } from '@mantine/core'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import AOS from 'aos'
import type { AppContext, AppProps } from 'next/app'
import App from 'next/app'
import qs from 'qs'
import { useEffect, useState } from 'react'
import { ToastContainer } from 'react-toastify'

import DynamicMeta from '@/features/common/components/DynamicMeta'
import { IInstitute } from '@/features/institute/types'
import AuthProvider from '@/shared/providers/Auth'
import CurriculumProvider from '@/shared/providers/Curriculum'
import InstituteProvider from '@/shared/providers/Institute'
import PlayerProvider from '@/shared/providers/Player'
import { convertCaseList, ConvertType } from '@/shared/utils/convertString'

export interface IAppProps extends AppProps {
  config: IPublicConfig | null
}

export default function MyApp({ Component, pageProps, config }: IAppProps) {
  const [instituteInfo, setInstituteInfo] = useState<IInstitute | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  })

  const hexToRgba = (hex: string) => {
    let r = parseInt(hex.substring(1, 3), 16)
    let g = parseInt(hex.substring(3, 5), 16)
    let b = parseInt(hex.substring(5, 7), 16)

    return `rgba(${r}, ${g}, ${b}, 0.05)`
  }

  useEffect(() => {
    AOS.init()
  }, [])

  useEffect(() => {
    if (!config) return
    if (typeof window.nodeEnv === 'undefined') window.nodeEnv = config.nodeEnv
    if (typeof window.baseUrl === 'undefined') window.baseUrl = config.baseUrl
    if (typeof window.apiEndpoint === 'undefined') window.apiEndpoint = config.apiEndpoint
    if (typeof window.oauthEndpoint === 'undefined') window.oauthEndpoint = config.oauthEndpoint
    if (typeof window.oauthRegisterEndpoint === 'undefined')
      window.oauthRegisterEndpoint = config.oauthRegisterEndpoint
    if (typeof window.oauthLogoutEndpoint === 'undefined')
      window.oauthLogoutEndpoint = config.oauthLogoutEndpoint
    if (typeof window.channelTalkKey === 'undefined') window.channelTalkKey = config.channelTalkKey
    if (typeof window.s3PresignedUrl === 'undefined') window.s3PresignedUrl = config.s3PresignedUrl
    if (typeof window.s3BucketName === 'undefined') window.s3BucketName = config.s3BucketName
    if (typeof window.bebridgeChatbotUrl === 'undefined')
      window.bebridgeChatbotUrl = config.bebridgeChatbotUrl
    if (typeof window.bebridgeChatbotPluginKey === 'undefined')
      window.bebridgeChatbotPluginKey = config.bebridgeChatbotPluginKey
    if (typeof window.bebridgeChatbotBotKey === 'undefined')
      window.bebridgeChatbotBotKey = config.bebridgeChatbotBotKey
    if (typeof window.bebridgeNoteUrl === 'undefined')
      window.bebridgeNoteUrl = config.bebridgeNoteUrl
    if (typeof window.bebridgeNoteUrlV2 === 'undefined')
      window.bebridgeNoteUrlV2 = config.bebridgeNoteUrlV2
    if (typeof window.bebridgeNotePluginKey === 'undefined')
      window.bebridgeNotePluginKey = config.bebridgeNotePluginKey
    if (typeof window.signInUrl === 'undefined') {
      const params = qs.stringify({
        response_type: 'code',
        client_id: config.oauthClientId,
        redirect_uri: config.oauthRedirectUrl,
        scope: config.oauthScope,
      })

      const nextUrl = `/oauth/authorize?${params}`
      window.signInUrl = `${config.oauthUrl}/users/b2b-login?next=${encodeURIComponent(nextUrl)}`
    }

    // 기관 별 색상 적용
    const fetchInstitute = () => {
      setIsLoading(true)
      fetch(`${config.apiEndpoint}/permissions/institutes?service_uri=${config.baseUrl}`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Error: ${response.status}`)
          }
          return response.json()
        })
        .then(data => {
          const instituteInfo = convertCaseList(data, ConvertType.CAMEL)
          setInstituteInfo(instituteInfo)
          document.documentElement.style.setProperty(
            '--primary-color',
            `${instituteInfo.mainColor}`,
          )
          document.documentElement.style.setProperty(
            '--primary-opacity',
            hexToRgba(`${instituteInfo.mainColor}`),
          )
        })
        .catch(error => {
          console.error('Fetch error:', error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    void fetchInstitute()
  }, [config])

  return (
    <>
      {instituteInfo && (
        <DynamicMeta
          {...{
            title: instituteInfo.name,
            ogTitle: instituteInfo.name,
            url: window.location.origin,
            ogImage: {
              property: 'og:image',
              content: instituteInfo.logoImage,
            },
            ogImageSecure: {
              property: 'og:image:secure_url',
              content: instituteInfo.logoImage,
            },
          }}
        />
      )}
      <MantineProvider>
        <QueryClientProvider client={queryClient}>
          <InstituteProvider instituteInfo={instituteInfo} isLoading={isLoading}>
            <AuthProvider>
              <CurriculumProvider>
                <PlayerProvider>
                  <ToastContainer
                    position="top-center"
                    icon={false}
                    draggable={false}
                    autoClose={3500}
                    hideProgressBar
                  />
                  <Component {...pageProps} />
                </PlayerProvider>
              </CurriculumProvider>
            </AuthProvider>
          </InstituteProvider>
        </QueryClientProvider>
      </MantineProvider>
    </>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext)
  const { req } = appContext.ctx
  let config = null

  if (typeof window === 'undefined') {
    const allConfig: IConfig = require('../shared/utils/config').default

    const host = req?.headers?.host || 'localhost'
    const protocol = req?.headers['x-forwarded-proto'] || 'http'
    const baseUrl = `${protocol}://${host}`

    config = {
      nodeEnv: allConfig.nodeEnv,
      baseUrl: baseUrl,
      oauthUrl: allConfig.oauthUrl,
      oauthEndpoint: allConfig.oauthEndpoint,
      oauthAuthorizationEndpoint: allConfig.oauthAuthorizationEndpoint,
      oauthClientId: allConfig.oauthClientId,
      oauthScope: allConfig.oauthScope,
      oauthRegisterEndpoint: allConfig.oauthRegisterEndpoint,
      oauthLogoutEndpoint: allConfig.oauthLogoutEndpoint,
      apiEndpoint: allConfig.apiEndpoint,
      oauthRedirectUrl: `${baseUrl}/oauth/callback`,
      channelTalkKey: allConfig.channelTalkKey,
      s3PresignedUrl: allConfig.s3PresignedUrl,
      s3BucketName: allConfig.s3BucketName,
      bebridgeChatbotUrl: allConfig.bebridgeChatbotUrl,
      bebridgeChatbotPluginKey: allConfig.bebridgeChatbotPluginKey,
      bebridgeChatbotBotKey: allConfig.bebridgeChatbotBotKey,
      bebridgeNoteUrl: allConfig.bebridgeNoteUrl,
      bebridgeNoteUrlV2: allConfig.bebridgeNoteUrlV2,
      bebridgeNotePluginKey: allConfig.bebridgeNotePluginKey,
    }
  }

  return { ...appProps, config }
}
